import { useQuery } from '@tanstack/react-query';
import { getCollectionDetections } from '@utils/api/collections';

/**
 * Fetches collection detections in normalized ([0-1] image-space) coordinates,
 * for precise per-image rendering on single-image projects. Separate from
 * useCollectionData (which stays on '3857' for the tree/filters/map-level layer)
 * because this dataset serves a different consumer and doesn't need the config
 * merge or tree-building work.
 *
 * Fails open: on error or while loading, `features` is simply `[]`, so the
 * per-image layer renders nothing rather than blocking or spinning — this must
 * never affect the primary 3857-driven tree/filter UI.
 *
 * @param {string} projectUuid
 * @param {string} collectionUuid
 * @param {boolean} enabled - gate fetching to single-image projects only
 * @returns {{ features: Array }}
 */
const useCollectionNormalizedFeatures = (projectUuid, collectionUuid, enabled) => {
	const { data } = useQuery({
		queryKey: ['collectionDetections', projectUuid, collectionUuid, 'normalized'],
		queryFn: () => getCollectionDetections(projectUuid, collectionUuid, 'normalized'),
		enabled: !!projectUuid && !!collectionUuid && enabled,
		staleTime: 10 * 60 * 1000,
		retry: false, // 404 means no detections exist yet — retrying won't help
	});

	return { features: data?.results?.features ?? [] };
};

export default useCollectionNormalizedFeatures;
